import { Component } from '@theme/component';
import {
  StandardEvents,
  CartLinesUpdateEvent
} from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, installments CTA close-on-click, and refreshing the
 * cart drawer before it opens.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {boolean} */
  #isRefreshingDrawer = false;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    document.addEventListener(
      'click',
      this.#handleComplementAdd
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    document.removeEventListener(
      'click',
      this.#handleComplementAdd
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );
  }

  /**
   * Handles drawer opening.
   * Refreshes the drawer from Shopify first, then recalculates sticky state.
   */
  #handleDrawerOpen = async () => {
    try {
      await this.#refreshDrawerContent();
    } catch (error) {
      console.warn('[cart-drawer] Could not refresh drawer before opening:', error);
    }

    this.#updateStickyState();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this
        .querySelector('shopify-payment-terms')
        ?.shadowRoot
        ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Fetches the latest cart drawer section and replaces only the inner markup.
   * This keeps <theme-drawer> and <cart-drawer-component> mounted.
   */
  #refreshDrawerContent = async () => {
    if (this.#isRefreshingDrawer) return;

    this.#isRefreshingDrawer = true;

    try {
      const sectionUrl = new URL(window.location.href);

      sectionUrl.searchParams.delete('sections');
      sectionUrl.searchParams.set(
        'section_id',
        'cart-drawer-section'
      );

      const response = await fetch(sectionUrl.toString(), {
        headers: {
          Accept: 'text/html'
        },
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error(
          `Cart drawer refresh failed with status ${response.status}.`
        );
      }

      const html = await response.text();
      const parser = new DOMParser();
      const updatedDocument = parser.parseFromString(
        html,
        'text/html'
      );

      const currentInner = this.querySelector(
        '[data-hydration-key="cart-drawer-inner"]'
      );

      const updatedInner = updatedDocument.querySelector(
        '[data-hydration-key="cart-drawer-inner"]'
      );

      if (!currentInner || !updatedInner) {
        throw new Error(
          'Cart drawer inner content was not found.'
        );
      }

      currentInner.replaceWith(updatedInner);

      /*
       * Los scripts dentro del HTML reemplazado no se ejecutan solos.
       * Forzamos el recálculo del aviso de monto mínimo y del ZIP.
       */
      window.AlpesDeliveryCheckerRefresh?.();
      document.dispatchEvent(
        new CustomEvent('alpes:cart-refreshed')
      );

      requestAnimationFrame(() => {
        this.#updateStickyState();
      });
    } finally {
      this.#isRefreshingDrawer = false;
    }
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null
    );

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Adds a recommended product and refreshes only the inner cart markup.
   *
   * @param {MouseEvent} event
   */
  #handleComplementAdd = async (event) => {
    const target = event.target;

    if (!(target instanceof Element)) return;

    const button = target.closest('[data-cart-complement-add]');

    if (!(button instanceof HTMLButtonElement)) return;
    if (button.disabled) return;

    const variantId = Number(
      button.dataset.cartComplementAdd
    );

    if (!variantId) {
      console.warn(
        '[cart-complements] Missing variant ID.'
      );
      return;
    }

    const originalText =
      button.textContent?.trim() || 'Agregar';

    button.disabled = true;
    button.textContent = 'Agregando…';

    const deferredEventPromise =
      CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart-complement',
        lines: [
          {
            merchandiseId: variantId.toString(),
            quantity: 1
          }
        ],
        promise: deferredEventPromise.promise
      })
    );

    try {
      const root =
        window.Shopify?.routes?.root || '/';

      const addResponse = await fetch(
        `${root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1
              }
            ]
          })
        }
      );

      const addData = await addResponse
        .json()
        .catch(() => null);

      if (!addResponse.ok) {
        throw new Error(
          addData?.description ||
          addData?.message ||
          'No se pudo agregar el producto.'
        );
      }

      await this.#refreshDrawerContent();

      const cartResponse = await fetch(
        `${root}cart.js`,
        {
          headers: {
            Accept: 'application/json'
          },
          cache: 'no-store'
        }
      );

      if (!cartResponse.ok) {
        throw new Error(
          'El producto se agregó, pero no se pudo sincronizar el contador.'
        );
      }

      const updatedCart =
        await cartResponse.json();

      deferredEventPromise.resolve({
        cart:
          CartLinesUpdateEvent.createCartFromAjaxResponse(
            updatedCart
          ),
        detail: {
          items: updatedCart.items,
          source: 'cart-complement',
          sourceId: 'cart-drawer-complements',
          itemCount: 1,
          didError: false
        }
      });

      requestAnimationFrame(() => {
        this.#updateStickyState();
      });
    } catch (error) {
      deferredEventPromise.reject(error);
      console.error(
        '[cart-complements]',
        error
      );

      button.disabled = false;
      button.textContent = 'Intenta de nuevo';

      window.setTimeout(() => {
        button.textContent = originalText;
      }, 2000);
    }
  };

  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    const content = dialog.querySelector(
      '.cart-drawer__content'
    );

    const summary = dialog.querySelector(
      '.cart-drawer__summary'
    );

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );
      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (
  !customElements.get('cart-drawer-component')
) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}
